import { FCX, includesInLC, startsFromInLC } from "@datassential/platform-ui-lib";
import { useTrendContext } from "@pages/Trend/TrendContext";
import { useCommonDataContext } from "@core/CommonDataContext";
import { useHistoricTrendContext } from "./HtContext";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import { TableColumnsContext } from "@components/Table/TableColumns";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { VirtualizedTable } from "@components/Table";
import cn from "classnames";
import { useFilterSort } from "@core/hooks/useFilterSort";
import {
	NAME_COL_WIDTH,
	NUM_COL_WIDTH,
	useHistoricTrendTableConfig
} from "@pages/Trend/components/HistoricTrend/HtTableConfig";
import { useEffect, useMemo } from "react";
import { HistoricTrendTableItem } from "@core/api/useHistoricTrend";
import { Sort } from "@core/models";
import { CHART_COLORS } from "@core/constants";
import { useSelectFilters } from "@vms/SelectFilters/SelectFiltersContext";
import { useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";

interface HtTableProps {
	selectedItems?: Set<number>;
	setSelectedItems?: (items: Set<number>) => void;
}

const HistoricTrendTable: FCX<HtTableProps> = ({
	selectedItems: externalSelectedItems,
	setSelectedItems: externalSetSelectedItems,
	className,
	...props
}) => {
	const {
		search,
		setIsMounted,
	} = useTrendContext();

	const { getMatchFilters } = useSelectFilters()

	const {
		setSelectedItems,
		data: rawData,
	} = useHistoricTrendContext();

	const {
		surveys,
		keywords
	} = useCommonDataContext();

	const isSearchApplied = useMemo(
		() => search.trim().length > 0,
		[search]
	);

	const [dataForSorting, surveyIds] = useMemo(() => {
		const surveyIds = new Set<number>();
		const tableData: HistoricTrendTableItem[] = [];

		const preSortedData = Array.from(Object.values(rawData)).sort(
			(a, b) => (a.name ?? '').localeCompare(b.name ?? '')
		);

		preSortedData.forEach((item) => {
            const tableItem: HistoricTrendTableItem = { ...item };

            item.surveys.forEach((survey) => {
				surveyIds.add(survey.surveyId);
                tableItem[survey.surveyId] = survey.valueNum ?? null;
			});

			if (search?.trim() && item.name && !includesInLC(item.name, search.trim())) {
				return;
			}

			const keyword = keywords?.[item.id ?? 0];

			if (!keyword || !getMatchFilters(keyword)) return;

			tableData.push(tableItem);
		});

		return [tableData, Array.from(surveyIds).sort((a, b) => b - a)];
	}, [rawData, search, keywords, getMatchFilters]);

	const sortedBySearch = useMemo(
		() => {
			if (!isSearchApplied) return dataForSorting;

			return [
				...dataForSorting.filter(i => startsFromInLC(i.name ?? '', search.trim())),
				...dataForSorting.filter(i => !startsFromInLC(i.name ?? '', search.trim())),
			];
		},
		[dataForSorting, isSearchApplied, search]
	);

	const {
		data: sortedData,
		sortingKey,
		sortingDirection,
		setSortingKey,
		setSortingDirection,
    } = useFilterSort(dataForSorting, "averageNum", Sort.Desc);

	const data = useMemo(
		() => isSearchApplied ? sortedBySearch : sortedData,
		[isSearchApplied, sortedBySearch, sortedData]
	);

	useEffect(() => {
		setIsMounted((prev) => {
			if (!data.length) return prev;

			if (!prev) {
				// Set the first 5 items as selected
				const firstFiveItems = data.slice(0, 5);
				setSelectedItems(firstFiveItems.map((item, index) => ({
					id: item.id ?? 0,
					color: CHART_COLORS[index],
				})));
			}

			return true
		});
	}, [data, setSelectedItems, setIsMounted]);

	const surveyColumns = useMemo(() => {
		return surveyIds.slice(0,20).map((surveyId) => {
			const survey = surveys?.find((survey) => survey.id === surveyId);

			return {
				id: surveyId,
				name: survey?.name ?? `Survey ${surveyId}`,
			};
		});
	}, [surveyIds, surveys]);

	const sortContextValue = useMemo(
		(): TableSortingContextType => ({
			value: {
				id: sortingKey,
				direction: sortingDirection,
			},
			options: [
				{
					id: "name",
					name: "name",
					defaultDirection: Sort.Asc,
				},
				{
					id: "averageNum",
					name: "averageNum",
				},
				{
					id: "changeNum",
					name: "changeNum",
				},
				...surveyColumns.map(col => ({
					id: col.id,
					name: col.name,
				})),
			],
			setValue: ({ id, direction }) => {
				setSortingKey(id as keyof HistoricTrendTableItem);
				setSortingDirection(direction);
			},
		}),
		[sortingKey, sortingDirection, setSortingKey, setSortingDirection, surveyColumns]
	);


	const tableConfig = useHistoricTrendTableConfig(
		data,
		surveyColumns,
		isSearchApplied,
	);
	const tableProviderValue = useMemo(
		() => ({
			data: tableConfig,
		}),
		[tableConfig]
	);

	const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length)

	const tableKey = useMemo(() => {
		return `${data.length}-${Date.now()}`;
	}, [data]);

	return (
		<div className={cn(
			"HtTable",
			"appears-top",
			"del-2",
		)}>
			<AsideButtons>
				<CopyTableButton
					data={copyTableContent}
					testId="buttonHistoricTrendCopyTable"
				/>
			</AsideButtons>
			<TableSortingContext.Provider value={sortContextValue}>
				<TableColumnsContext.Provider value={tableProviderValue}>
					<div
						className={cn("HtTable__fixed-table", className)}
						{...props}
					>
						<VirtualizedTable
							className="HtVirtualizedTable"
							headHeight={48}
							rowHeight={42}
							key={tableKey}
							rowsCount={data.length}
							testId="tableHistoricTrendVirtualized"
							contentWidthPx={(surveyColumns.length + 2) * NUM_COL_WIDTH + NAME_COL_WIDTH}
							showFallback
						/>
					</div>
				</TableColumnsContext.Provider>
			</TableSortingContext.Provider>
		</div>
	);
};

export default HistoricTrendTable;